/**
 * As decisões da régua do painel que não são desenho: como os dias viram colunas
 * e vãos, que dia começa marcado e como cada dia se escreve. A conta (quantos
 * vencem, o que não conta) vem pronta do backend — services/prazos/regua.py.
 */
import type { DiaDaRegua } from '@/api/regua';

/** Uma coluna (dia que conta) ou um vão (dias seguidos que não contam). */
export type Trecho = { tipo: 'dia'; dia: DiaDaRegua; indice: number } | { tipo: 'vao'; dias: DiaDaRegua[] };

export function trechos(dias: DiaDaRegua[]): Trecho[] {
  const saida: Trecho[] = [];
  dias.forEach((dia, indice) => {
    const anterior = saida[saida.length - 1];
    if (dia.conta) saida.push({ tipo: 'dia', dia, indice });
    else if (anterior?.tipo === 'vao') anterior.dias.push(dia);
    else saida.push({ tipo: 'vao', dias: [dia] });
  });
  return saida;
}

/** Ao meio-dia: `new Date('2026-10-02')` é meia-noite UTC, que no Brasil ainda é dia 1. */
export function aoMeioDia(iso: string): Date {
  return new Date(`${iso.slice(0, 10)}T12:00:00`);
}

/** "hoje", "amanhã" ou o dia da semana curto ("qua"). */
export function rotuloCurto(iso: string, hoje: string): string {
  const diferenca = Math.round((aoMeioDia(iso).getTime() - aoMeioDia(hoje).getTime()) / 86_400_000);
  if (diferenca === 0) return 'hoje';
  if (diferenca === 1) return 'amanhã';
  return aoMeioDia(iso).toLocaleDateString('pt-BR', { weekday: 'short' }).replace(/\.$/, '');
}

/** "Amanhã — Terça-feira, 20 de outubro". */
export function rotuloLongo(iso: string, hoje: string): string {
  const d = aoMeioDia(iso);
  const semana = d.toLocaleDateString('pt-BR', { weekday: 'long' });
  const extenso = `${semana.charAt(0).toUpperCase()}${semana.slice(1)}, ${d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })}`;
  const curto = rotuloCurto(iso, hoje);
  if (curto === 'hoje') return `Hoje — ${extenso}`;
  if (curto === 'amanhã') return `Amanhã — ${extenso}`;
  return extenso;
}

/** "20 de outubro" / "2 de novembro". */
export function diaEMes(iso: string): string {
  return aoMeioDia(iso).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' });
}

/** A régua começa marcando hoje; num vão (sábado, suspensão de hoje), o próximo dia que conta. */
export function diaInicial(dias: DiaDaRegua[]): string | null {
  return dias.find((d) => d.conta)?.data ?? dias[0]?.data ?? null;
}

/** O primeiro dia com prazo depois de hoje ("Nada vence hoje · próximo: …"). */
export function proximoComPrazo(dias: DiaDaRegua[]): DiaDaRegua | null {
  return dias.slice(1).find((d) => d.conta && d.prazos > 0) ?? null;
}

export function plural(n: number, um: string, varios: string): string {
  return `${n} ${n === 1 ? um : varios}`;
}

/** O endereço do acervo filtrado pelos prazos de um intervalo (um dia: de = até). */
export function acervoQueVence(de: string, ate: string = de): string {
  return `/app/processos?vence_de=${de}&vence_ate=${ate}`;
}

/** A data de hoje em Brasília (a régua vem do servidor no mesmo fuso). */
export function hojeLocal(agora: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(agora);
}

export function somarDias(iso: string, dias: number): string {
  const [a, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, d + dias)).toISOString().slice(0, 10);
}
